import {
    Component,
    Input,
    Output,
    ViewChild,
    ViewEncapsulation,
    EventEmitter,
    ChangeDetectorRef,
    ElementRef,
    Renderer2,
} from '@angular/core';
import {
    IDep,
    IFilial,
    IRecTalon,
    IRnumbDate,
    ITalonModal,
} from 'src/app/interfaces/record.interface';
import { RecordService } from 'src/app/services/record.service';
import { strToDate } from '../application/global.function';
import { MatCalendar } from '@angular/material/datepicker';
import moment, { Moment } from 'moment';
import { CalendarHeader } from './header/calendar-header.component';
import { AlertService } from 'src/lib/ariadna/alert';
import { NewTalonComponentMobile } from 'src/app/modals/new-talon-mobile/talon.component';
import { MatDialog } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-talonpicker',
    templateUrl: './talonpicker.component.html',
    styleUrls: ['./talonpicker.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TalonpickerComponent {
    @Input() params!: IRnumbDate;
    @Output() talonPickerClosed = new EventEmitter();
    @ViewChild('calendar') calendar!: MatCalendar<Moment>;
    loading: boolean = true;
    activeDays: Moment[] = []; // NOT sorted by date
    selectedDay: Moment | null = moment();
    minDate: Moment = moment();
    maxDate: Moment = moment().add(1, 'month');

    talonListAll: IRecTalon[] = [];
    talonListFiltered: IRecTalon[] = [];
    depList: IDep[] = [];
    selectedDep: number | null = null;
    selectedDeps: Array<number> = []; // Список выбранных депов
    header = CalendarHeader;
    pickedTalon: IRecTalon | null = null;
    filialFromParams: IFilial[] = [];
    isClosing: boolean = false;
    @ViewChild('pickerContainer', { read: ElementRef })
    pickerContainer!: ElementRef;

    dateFilter = (date: Moment) => this.isActiveDay(date);

    constructor(
        private rs: RecordService,
        private alertService: AlertService,
        private modal: MatDialog,
        private cdref: ChangeDetectorRef,
        private elementRef: ElementRef,
        private renderer: Renderer2,
    ) {}

    async ngOnChanges(changes: any) {
        this.filialFromParams = this.params?.Idep
            ? Array.isArray(this.params.Idep)
                ? this.params.Idep
                : [this.params.Idep]
            : [];

        if (changes.params) {
            this.loading = true;

            try {
                this.maxDate = moment(changes.params.currentValue.periodEnd);
                await Promise.all([
                    await this.getTalonList(changes.params.currentValue),
                    await this.getDepList(changes.params.currentValue),
                ]);
                // здесь мне надо заполнить селектед депс кровь из носа
                this.selectedDep =
                    this.depList.find(
                        (dep) =>
                            dep.text === this.filialFromParams[0]?.dep_name,
                    )?.keyid || null;
                this.selectedDeps =
                    this.depList.find(
                        (dep) =>
                            dep.text === this.filialFromParams[0]?.dep_name,
                    )?.mergedKeyIds || [];
                this.activeDays = this.filterActiveDays();
                this.selectedDay = this.findFirstActiveDay();
                this.talonListFiltered = this.filterTalons();
                this.updateCalendar();
                this.loading = false;
                this.pickedTalon = null;
            } catch (e) {
                this.alertService.error('Ошибка получения номерков');
                setTimeout(() => {
                    this.closeCalendar(); // Delay closing to allow user to see the error
                }, 1000);
                this.loading = false;
                console.error(e);
            }
        }
    }
    ngAfterViewInit() {
        // Добавляем класс 'opened' на внутренний div
        setTimeout(() => {
            if (this.pickerContainer) {
                this.renderer.addClass(
                    this.pickerContainer.nativeElement,
                    'opened',
                );
            }
        }, 100);
    }
    private async getTalonList(params: IRnumbDate) {
        try {
            this.talonListAll = await lastValueFrom(
                this.rs.getRnumbListv2(params),
            );

            this.talonListAll.forEach((talon, i) => {
                this.talonListAll[i].dtBegin = strToDate(talon.dat_begin_str);
            });
        } catch (error) {
            throw new Error('Failed to fetch talon list');
        }
    }
    private async getDepList(params: IRnumbDate) {
        try {
            const fullDepList: Array<IDep> = await lastValueFrom(
                this.rs.getDepListv2(params),
            ); // Получаем полный список депов

            const depsWithTalons = new Set(
                this.talonListAll.map((talon) => Number(talon.depid)),
            ); // Создаем уникальное множество depid из talonListAll, то есть берем депы только от активных номерков

            const filteredByTalons = fullDepList.filter((dep: IDep) =>
                depsWithTalons.has(Number(dep.keyid)),
            ); // Теперь фильтруем изначально полученный список депов беря только те, у которых есть талоны

            this.depList = this.mergeDuplicByText(filteredByTalons);
        } catch (error) {
            console.error('Failed to fetch department list', error);
            throw new Error('Failed to fetch department list');
        }
    }

    private mergeDuplicByText(deps: IDep[]): IDep[] {
        const map = new Map<string, IDep>();
        for (const dep of deps) {
            const existing = map.get(dep.text);
            if (existing) {
                existing.mergedKeyIds!.push(dep.keyid);
            } else {
                map.set(dep.text, { ...dep, mergedKeyIds: [dep.keyid] });
            }
        }
        return Array.from(map.values());
    }

    selectDay(day: Moment | null) {
        this.talonListFiltered = this.filterTalons();
    }

    selectDep(dep: IDep) {
        this.pickedTalon = null;
        this.selectedDep = dep.keyid;
        if (dep.mergedKeyIds && dep.mergedKeyIds.length > 1) {
            this.selectedDeps = dep.mergedKeyIds;
        }
        this.dateFilter = (date: Moment | null): boolean => {
            if (!date) return false;

            const filteredTalons = this.selectedDep
                ? this.talonListAll.filter(
                      (talon) => talon.depid === this.selectedDep,
                  )
                : this.talonListAll;

            return filteredTalons.some((talon) => {
                const talonDate = moment(talon.dtBegin);
                return (
                    talonDate.format('YYYY-MM-DD') ===
                    moment(date).format('YYYY-MM-DD')
                );
            });
        };
        this.talonListFiltered = this.filterTalons();
        this.cdref.detectChanges();
    }

    updateCalendar() {
        if (this.calendar) this.calendar.updateTodaysDate();
    }

    private isActiveDay(day: Moment) {
        return this.activeDays.some(
            (activeDay) =>
                day.date() === activeDay.date() &&
                day.month() === activeDay.month() &&
                day.year() === activeDay.year(),
        );
    }

    private filterActiveDays() {
        return this.talonListAll
            .map((talon) => moment(strToDate(talon.dat_begin_str)))
            .filter((day, index, self) => self.indexOf(day) === index);
    }

    findFirstActiveDay() {
        let firstActiveDay = this.activeDays[0];
        this.activeDays.forEach((day) => {
            if (firstActiveDay.valueOf() > day.valueOf()) firstActiveDay = day;
        });
        return firstActiveDay;
    }

    filterTalons() {
        return this.talonListAll.filter((talon) => {
            return (
                talon.dtBegin?.getFullYear() === this.selectedDay?.year() &&
                talon.dtBegin?.getMonth() === this.selectedDay?.month() &&
                talon.dtBegin?.getDate() === this.selectedDay?.date() &&
                (this.selectedDep
                    ? this.selectedDeps.length > 1 &&
                      this.selectedDeps.includes(this.selectedDep)
                        ? this.selectedDeps.includes(talon.depid)
                        : talon.depid === this.selectedDep
                    : true)
            );
        });
    }
    // event: MouseEvent, talon: IRecTalon
    async openTalonModal(event: MouseEvent, talon: IRecTalon | null) {
        event.stopPropagation();
        // this.loading = true;

        if (talon?.rnumbid !== this.pickedTalon?.rnumbid) {
            this.pickedTalon = talon;
        }
        if (this.params?.srv?.is_telemed !== talon?.is_telemed) {
            this.loading = false;
            return;
        }

        if (!talon) return;
        let data: ITalonModal = {
            rnumbID: null,
            info: {
                docdepID: talon?.docdepid as number,
                beginDate: talon?.dtBegin,
            },
            srv: this.params.srv,
            paramCansel: false,
            infoDoc: this.params.infoDoc,
        };

        try {
            if (
                talon?.is_interval &&
                talon.is_interval > 0 &&
                talon.interval_id
            ) {
                // console.log(talon);
                const { guid, err_code, err_text } = await lastValueFrom(
                    this.rs.getRnumbCreateInterval(
                        talon?.interval_id,
                        talon?.dat_begin_str,
                        talon?.dat_end_str,
                    ),
                );

                if (err_code !== 0 || !guid) throw new Error(err_text);
                data.rnumbID = guid;
                await lastValueFrom(
                    this.rs.setRnumbBlStatus(data.rnumbID as number),
                );
            } else if (talon?.rnumbid) {
                data.rnumbID = talon.rnumbid;

                await lastValueFrom(
                    this.rs.setRnumbBlStatus(data.rnumbID as number),
                );
            }

            this.alertService.success(
                `Талон успешно зарезервирован, № талона: ${data.rnumbID}`,
            );
            let dialogRefOptions = {
                width: 'auto',
                height: 'auto',
                maxWidth: 'auto',
                minWidth: 'auto',
                closeOnNavigation: true,
                data,
            };

            // Check screen width and adjust dialog options if needed
            if (window.matchMedia('(max-width: 768px)').matches) {
                dialogRefOptions.width = '100vw';
                dialogRefOptions.maxWidth = '100vw';
                dialogRefOptions.minWidth = '100vw';
            }
            const modalRef = this.modal.open(
                NewTalonComponentMobile,
                dialogRefOptions,
            );
            this.loading = false;
            modalRef.afterClosed().subscribe((result) => {
                // console.log(result);
                if (result && result.unlockTalon) {
                    lastValueFrom(this.rs.getRnumbUnlock(result.rnumbID))
                        .then((value) => {
                            this.alertService.success(
                                `Талон успешно разблокирован, № талона: ${result.rnumbID}`,
                            );
                        })
                        .catch((error: any) => {
                            this.alertService.error(
                                `Не удалось разблокировать талон с id ${result.rnumbID}: ${error.msg}`,
                            );
                        });
                }
            });
        } catch (error: any) {
            console.error(`Не удалось создать интервал: ${error.msg}`);
            this.alertService.error(
                `Не удалось записаться на номерок: ${error.msg}`,
            );
        }
        this.loading = false;
    }

    clickTalon(event: Event, talon: IRecTalon) {
        event.stopPropagation();

        if (this.params?.srv?.is_telemed !== talon?.is_telemed) {
            return;
        }

        if (talon.rnumbid !== this.pickedTalon?.rnumbid) {
            this.pickedTalon = talon;
        } else {
            this.pickedTalon = null;
        }
    }

    closeCalendar() {
        // this.talonPickerClosed.emit();
        this.isClosing = true;

        setTimeout(() => {
            this.talonPickerClosed.emit();
        }, 300);
    }

    /* Блокировка выбора талона */
    talonDisable(talon: IRecTalon): boolean {
        if (!this.params?.srv) return false;
        return this.params.srv.is_telemed === talon.is_telemed;
    }
}
